import { IconRefresh } from "@tabler/icons-react";
import {
  Badge, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Group, RegionCargando, Skeleton, Stack, Text,
} from "@/components/ui";
import { formatFechaHora } from "@/shared/format/fecha";
import { useEnvio, useReenviarEnvio } from "../../hooks/use-notificaciones";
import { ETIQUETA_CANAL } from "../../utils/lienzo";
import { ESTADO_ENVIO } from "./estado-envio";

const ETIQUETA_CAMPO: Record<string, string> = {
  asunto: "Asunto",
  cuerpoHtml: "Cuerpo (HTML)",
  cuerpoJson: "Cuerpo JSON",
  titulo: "Título",
  texto: "Texto",
  enlace: "Enlace",
  cuerpo: "Cuerpo",
  contentSid: "Plantilla (Content SID)",
  variables: "Valores de los marcadores",
  markdown: "MarkdownV2",
};

/** Detalle de un envío: contenido final, historial de intentos y reenvío manual. */
export function EnvioDetalleDialog({ envioId, onCerrar }: { envioId: string | null; onCerrar: () => void }) {
  const { data, isLoading } = useEnvio(envioId);
  const reenviar = useReenviarEnvio();
  const e = data?.envio;
  const reenviable = e?.estado === "Fallido" || e?.estado === "NoEntregado";

  return (
    <Dialog open={envioId !== null} onOpenChange={(o) => (!o ? onCerrar() : undefined)}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalle del envío</DialogTitle>
        </DialogHeader>
        {isLoading || !data || !e ? (
          <EnvioDetalleSkeleton />
        ) : (
          <Stack gap="md">
            <Group gap="xs">
              <Badge color={ESTADO_ENVIO[e.estado]?.color ?? "slate"}>{ESTADO_ENVIO[e.estado]?.etiqueta ?? e.estado}</Badge>
              <Badge color="slate">{ETIQUETA_CANAL[e.canal] ?? e.canal}</Badge>
              {e.esPrueba ? <Badge color="info">Prueba</Badge> : null}
              <Text size="xs" c="dimmed">Flujo v{e.versionFlujo} · {e.notificacionNombre}</Text>
            </Group>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Destinatario</dt>
              <dd className="break-all">{e.destinatarioNombre ? `${e.destinatarioNombre} · ` : ""}{e.destinatario}</dd>
              <dt className="text-muted-foreground">Origen</dt>
              <dd>{data.origenDestinatario}</dd>
              <dt className="text-muted-foreground">Creado</dt>
              <dd>{formatFechaHora(e.creadoAt)}</dd>
              {e.enviadoAt ? (<><dt className="text-muted-foreground">Enviado</dt><dd>{formatFechaHora(e.enviadoAt)}</dd></>) : null}
              {e.proximoIntentoAt && e.estado === "Reintentando" ? (<><dt className="text-muted-foreground">Próximo intento</dt><dd>{formatFechaHora(e.proximoIntentoAt)}</dd></>) : null}
              {e.motivoOmision ? (<><dt className="text-muted-foreground">Motivo</dt><dd>{e.motivoOmision}</dd></>) : null}
              {data.proveedorMensajeId ? (<><dt className="text-muted-foreground">Respuesta</dt><dd>{data.proveedorMensajeId}</dd></>) : null}
            </dl>

            {Object.keys(data.contenido).length > 0 ? (
              <Stack gap="xs">
                <Text fw={600} size="sm">Contenido enviado</Text>
                {Object.entries(data.contenido).map(([campo, valor]) => (
                  <div key={campo}>
                    <Text size="xs" c="dimmed">{ETIQUETA_CAMPO[campo] ?? campo}</Text>
                    <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md border border-border bg-muted/40 p-2 font-mono text-xs">
                      {valor || "—"}
                    </pre>
                  </div>
                ))}
              </Stack>
            ) : null}

            {data.historial.length > 0 ? (
              <Stack gap="xs">
                <Text fw={600} size="sm">Intentos</Text>
                <ol className="space-y-1 text-sm">
                  {data.historial.map((i) => (
                    <li key={`${i.numero}-${i.at}`} className="flex gap-2">
                      <Badge color={i.exitoso ? "exito" : "peligro"} size="xs">#{i.numero}</Badge>
                      <span className="text-muted-foreground">{formatFechaHora(i.at)}</span>
                      <span className="break-all">{i.exitoso ? "Aceptado" : i.mensaje}</span>
                    </li>
                  ))}
                </ol>
              </Stack>
            ) : null}

            {data.eventoPayload ? (
              <details>
                <summary className="cursor-pointer text-sm text-muted-foreground">Datos del evento</summary>
                <pre className="mt-2 max-h-64 overflow-auto rounded-md border border-border bg-muted/40 p-2 font-mono text-xs">
                  {formatearJson(data.eventoPayload)}
                </pre>
              </details>
            ) : null}
          </Stack>
        )}
        <DialogFooter className="mt-4">
          <Button variant="default" onClick={onCerrar}>Cerrar</Button>
          {reenviable && e ? (
            <Button leftSection={<IconRefresh size={14} />} loading={reenviar.isPending} onClick={() => reenviar.mutate(e.id)}>
              Reenviar
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Primera carga · badges, pares etiqueta-valor y un bloque de contenido. */
function EnvioDetalleSkeleton() {
  return (
    <RegionCargando etiqueta="Cargando detalle del envío…">
      <Stack gap="md">
        <Group gap="xs" aria-hidden="true">
          <Skeleton height={20} width={80} className="rounded-full" />
          <Skeleton height={20} width={64} className="rounded-full" />
          <Skeleton height={10} width={180} />
        </Group>
        <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2.5" aria-hidden="true">
          {[["70px", "60%"], ["50px", "35%"], ["56px", "30%"], ["60px", "30%"]].map(([et, val], i) => (
            <div key={i} className="contents">
              <Skeleton height={12} width={et} />
              <Skeleton height={12} width={val} />
            </div>
          ))}
        </div>
        <Stack gap="xs" aria-hidden="true">
          <Skeleton height={14} width={140} />
          <Skeleton height={10} width={60} />
          <Skeleton height={96} />
        </Stack>
      </Stack>
    </RegionCargando>
  );
}

function formatearJson(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
